import {DirectMessageDelivery, DirectMessageStatus} from "@/api/dto/care-journey";
import React, {Fragment, useContext} from "react";
import CustomExpandMoreWidget from "@common/CustomExpandMoreButton";
import Icon, {ICONS} from "patient-ping-remedy/packages/icon";
import Spacer from "patient-ping-remedy/packages/spacer";
import {colors, sizes} from "patient-ping-remedy/packages/theme";
import {
  CareNoteDirectAddress,
  CareNoteDownloadButton,
  CareNotStatus,
  StandardSmallTypography
} from "@/components/pages/tasks/task_edit/progress/CareNoteSection.styles";
import {css} from "@emotion/css";
import DisplayHelpers from "@/helpers/display_helpers";
import {StyleType} from "patient-ping-remedy/packages/button";
import FileHelpers from "@/helpers/file_helpers";
import {CarecoApiContext} from "@/app-context/careco-api-context";
import {useActiveRoster} from "@/store/roster_store";
import {useMixpanel} from "@/hooks/useMixpanel";
import {MixpanelEventType, MixpanelTaskEvent} from "@/api/dto/mixpanel";
import {HintTypography} from "patient-ping-remedy/packages/typography";
import Helpers from "@/helpers/helpers";

interface Props {
  directMessage: DirectMessageDelivery
  eventType: string
  careJourneyName: string
}

const DELIVERY_STATUS_MAPPING = {
  [DirectMessageStatus.DELIVERED]: {
    title: 'Sent successfully',
    iconClass: ICONS["check-circle"],
    iconColor: colors.green5,
    date: (dm: DirectMessageDelivery) => dm.deliveredAt,
    suffix: 'Sent',
    additionalDetails: undefined,
  },
  [DirectMessageStatus.PENDING]: {
    title: 'Sending in progress',
    iconClass: ICONS["spinner"],
    iconColor: colors.green4,
    date: (dm: DirectMessageDelivery) => dm.createdAt,
    suffix: 'Initiated',
    additionalDetails: <HintTypography>This may take up to 1 hour to complete.</HintTypography>
  },
  [DirectMessageStatus.FAILED]: {
    title: 'Failed to send',
    iconClass: ICONS["exclamation-triangle"],
    iconColor: colors.yellow5,
    date: (dm: DirectMessageDelivery) => dm.createdAt,
    suffix: 'Initiated',
    additionalDetails: undefined,
  },
};

const CareNoteDeliveryStatus = ({directMessage, eventType, careJourneyName}: Props) => {
  const {carecoApi} = useContext(CarecoApiContext);
  const currentRoster = useActiveRoster();
  const {mixpanelEventProperties, getCurrentGroupDetails} = useMixpanel();
  const [inFlight, setInFlight] = React.useState(false);

  const handleDownload = async () => {
    if (inFlight) return;
    setInFlight(true);
    const response = await carecoApi?.getCareJourneyAttachment(directMessage.attachmentId, currentRoster!.id)
      .catch(error => {
        Helpers.alertOnError(`Failed to download care note. ${Helpers.traceId(error)}`, error);
      })
      .finally(() => setInFlight(false));
    if (response) {
      const arrayBuffer = FileHelpers.base64ToArrayBuffer(response);
      const blob = new Blob([arrayBuffer]);
      FileHelpers.downloadFile(URL.createObjectURL(blob), "Care note.pdf");

      if (mixpanelEventProperties && currentRoster) {
        const event: MixpanelTaskEvent = {
          eventName: MixpanelEventType.BIH_CARE_NOTE_DOWNLOADED,
          eventType,
          careJourneyName,
          ...mixpanelEventProperties,
          ...getCurrentGroupDetails(currentRoster)
        };
        carecoApi?.postMixpanelEvent(event);
      }
    }
  };

  const mapping = DELIVERY_STATUS_MAPPING[directMessage.status];
  const getContent = () => {
    const date = mapping.date(directMessage);
    return <div className={css({paddingLeft: '2.5rem'})}>
      <div className={css({display: 'flex', justifyContent: 'space-between'})}>
        <div>
          {(date || directMessage.initiatedBy) &&
            <StandardSmallTypography>
              {mapping.suffix}
              {directMessage.initiatedBy &&
                <> by {directMessage.initiatedBy.first_name} {directMessage.initiatedBy.last_name}</>
              }
              {date && <> on {DisplayHelpers.formatDateTimeLong(date, true)} </>}
            </StandardSmallTypography>
          }
          {mapping.additionalDetails && <div>{mapping.additionalDetails}</div>}
        </div>
        {directMessage.status === DirectMessageStatus.FAILED &&
          <CareNoteDownloadButton
            id={"download-failed-care-note-button"}
            onClick={handleDownload}
            styleType={StyleType.TERTIARY}
            disabled={inFlight}
          >
            <Icon iconClass={ICONS["download"]}/>
            <Spacer itemWidth={sizes.xxSmall}/>
            Download Care note
          </CareNoteDownloadButton>
        }
      </div>
    </div>;
  };

  return <div className={css({marginTop: sizes.xSmall})}>
    <CustomExpandMoreWidget
      arrowPosition={'right'}
      children={getContent()}
      title={<Fragment>
        <Icon
          iconClass={mapping.iconClass}
          color={mapping.iconColor}
          weight={"far"}
          iconSize={"1x"}
        />
        <Spacer itemWidth={sizes.xSmall}/>
        <CareNotStatus>
          {mapping.title}
        </CareNotStatus>
        <CareNoteDirectAddress>
          to {directMessage.directAddress}
        </CareNoteDirectAddress>
      </Fragment>}
    />
  </div>;
};

export default CareNoteDeliveryStatus;
